import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchResourceWithPost } from '@app/src/api/fetchHooks'
import { CATEGORY_FILTER_DRAWER_ALLOWED_FILTERS } from '@app/src/components/CreateNewRequestModal/Common/SelectFilterStep'
import { calculateStartsAndEndsAtDate } from '@app/src/components/CreateNewRequestModal/utils'
import FilterDisplayChip from '@app/src/components/FilterDisplayChip'
import StyledChip from '@app/src/components/StyledChip'
import { getAssessmentChipLabels } from '@app/src/hooks/getAssessmentFilterLabel'
import { useGetApiQueryFilters } from '@app/src/hooks/queryFilters'
import useActiveRiskIndexByType from '@app/src/hooks/useActiveRiskIndexByType'
import { useAssessmentTemplatesWithSections } from '@app/src/hooks/useAssessmentTemplatesWithSections'
import { useGetAutomatedAssessmentChips as useGetRiskStatusAndCategoryChips } from '@app/src/hooks/useGetAutomatedAssessmentChips'
import useOrganizationSettings from '@app/src/hooks/useOrganizationSettings'
import { usePeriodName } from '@app/src/hooks/usePeriodName'
import { useRiskStatus } from '@app/src/hooks/useRiskStatus'
import type { AutomationFormData } from '@app/src/pages/Configurations/ConfigurationsPages/Automation/CreateAutomationModal'
import TriggerActionApplyToSummary from '@app/src/pages/Configurations/ConfigurationsPages/Automation/CreationSteps/summaries/TriggerActionApplyToSummary'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { getDateWithoutTimeFromString } from '@app/src/utils/helpersTs'
import {
  AssessmentOutlined,
  CategoryOutlined,
  FilterListOutlined,
  InfoOutlined,
  ManageSearchOutlined,
  SaveAlt,
  SendOutlined,
} from '@mui/icons-material'
import { Alert, Box, Stack } from '@mui/material'
import React, { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { RequestAutomationType } from '../RequestAutomationType'
import { RequestAutomationSummary } from './summaries/RequestAutomationSummary'

const automationTranslationKeyMap: Record<RequestAutomationType, string> = {
  [RequestAutomationType.AI_SCREEN]: 'form.createAutomation.deadlineOffset.optionTextForProvidersInCategory',
  [RequestAutomationType.AI_ENRICH]: 'form.createAutomation.deadlineOffset.optionTextForProvidersInCategory',
  [RequestAutomationType.FOR_PROVIDERS_WITH_NEW_ASSESSMENT_LEVEL]:
    'form.createAutomation.deadlineOffset.optionTextForProvidersWithNewAssessmentLevel',
  [RequestAutomationType.FOR_EVERY_NEW_PROVIDER]: 'form.createAutomation.deadlineOffset.optionTextForEveryNewProvider',
  [RequestAutomationType.FOR_PROVIDERS_IN_CATEGORY]:
    'form.createAutomation.deadlineOffset.optionTextForProvidersInCategory',
  [RequestAutomationType.EXPECTATION]: 'form.createAutomation.deadlineOffset.optionTextForProvidersInCategory',
}

interface AffectedCountQuery {
  providerFilters: FilterGroup[]
  templateIds: number[]
  periodStartsAt: string | null
  periodEndsAt: string | null
  automationType: RequestAutomationType
}

const AutomationSummary: React.FC = () => {
  const { formatMessage, formatDate } = useIntl()
  const { getValues } = useFormContext<AutomationFormData>()
  const data = getValues()
  const templateNames = data.templates.map(t => t.title)
  const categoryFilters = data?.categoryFilters
  const filters = useGetApiQueryFilters(CATEGORY_FILTER_DRAWER_ALLOWED_FILTERS)
  const { getPeriodDisplayName } = usePeriodName()
  const { assessmentTemplates } = useAssessmentTemplatesWithSections()
  const { riskStatusOptions } = useRiskStatus()
  const { countryOptions, industryOptions } = useActiveRiskIndexByType()
  const { settings, isFetched: isSettingsFetched } = useOrganizationSettings({
    enabled: data.automationType === RequestAutomationType.AI_SCREEN,
  })

  const sourceFilter = useMemo(
    () => filters?.filter(filter => filter.name === 'assessments.isScreened') || [],
    [filters],
  )
  const riskAndCategoryChipFilters = useMemo(
    () => data.categoryFilters?.filter(filter => filter.name !== 'assessments.isScreened'),
    [data.categoryFilters],
  )

  const riskAndCategoryChips = useGetRiskStatusAndCategoryChips(
    riskAndCategoryChipFilters,
    riskStatusOptions,
    countryOptions,
    industryOptions,
  )

  const { periodEndsAt, periodStartsAt } = calculateStartsAndEndsAtDate({
    dateType: data.dateType,
    datePeriod: data.datePeriod,
    dateYear: data.dateYear,
  })
  const periodDisplayName = getPeriodDisplayName(periodStartsAt, periodEndsAt)

  const automationType = data.automationType

  const { data: eligibleCount, isLoading: isLoadingEnrichmentCount } = useFetchResourceWithPost<number>({
    key: FetchKey.EligibleEnrichmentCount,
    endpoint: endpoints.eligibleEnrichmentCount,
    body: [],
    options: {
      enabled: automationType === RequestAutomationType.AI_ENRICH,
    },
  })

  const templateIds = data.templates?.map(t => t.id) ?? []

  const { data: eligibleScreeningCount, isLoading: isLoadingScreeningCount } = useFetchResourceWithPost<number>({
    key: FetchKey.EligibleScreeningCount,
    endpoint: endpoints.eligibleScreeningCount,
    body: templateIds,
    options: {
      enabled: automationType === RequestAutomationType.AI_SCREEN && templateIds.length > 0,
    },
  })

  const shouldFetchAffectedCount =
    (automationType === RequestAutomationType.FOR_PROVIDERS_IN_CATEGORY ||
      automationType === RequestAutomationType.FOR_PROVIDERS_WITH_NEW_ASSESSMENT_LEVEL) &&
    (categoryFilters?.length ?? 0) > 0 &&
    (data.templates?.length ?? 0) > 0 &&
    Boolean(periodStartsAt) &&
    Boolean(periodEndsAt)

  const { data: affectedCount, isLoading: isLoadingAffectedCount } = useFetchResourceWithPost<
    number,
    AffectedCountQuery
  >({
    key: FetchKey.AutomationAffectedCount,
    endpoint: endpoints.affectedAutomationCount,
    body: {
      providerFilters: categoryFilters ?? [],
      templateIds: data.templates?.map(t => t.id) ?? [],
      periodStartsAt,
      periodEndsAt,
      automationType,
    },
    options: {
      enabled: shouldFetchAffectedCount,
    },
  })

  if (automationType === RequestAutomationType.AI_ENRICH) {
    return (
      <TriggerActionApplyToSummary
        triggerIcon={<SaveAlt />}
        triggerPrimaryId="form.createAutomation.summary.companyImportedOrAdded"
        actionIcon={<CategoryOutlined />}
        actionPrimaryId="form.createAutomation.summary.aiEnrichAction"
        actionSecondaryId="form.createAutomation.summary.aiEnrichProperties"
        bannerMessageId="form.createAutomation.applyTo.immediateEnrichmentBanner"
        affectedCount={eligibleCount}
        isLoadingCount={isLoadingEnrichmentCount}
        isAiAutomation
      />
    )
  }

  if (automationType === RequestAutomationType.AI_SCREEN) {
    const remainingScreenings = settings?.remainingProviderScreenings ?? 0

    return (
      <TriggerActionApplyToSummary
        triggerIcon={<SaveAlt />}
        triggerPrimaryId="form.createAutomation.summary.companyImportedOrAdded"
        actionIcon={<ManageSearchOutlined />}
        actionPrimaryId="form.createAutomation.summary.aiScreenAction"
        actionSecondaryId="form.createAutomation.summary.aiScreenTemplates"
        actionExtra={
          <Box display="flex" flexWrap="wrap" gap={1} pt={1} pl={7}>
            {templateNames.map(template => (
              <StyledChip key={template} label={template} />
            ))}
          </Box>
        }
        bannerMessageId="form.createAutomation.applyTo.immediateScreeningBanner"
        affectedCount={eligibleScreeningCount}
        isLoadingCount={isLoadingScreeningCount}
        creditInfo={
          isSettingsFetched ? { remaining: remainingScreenings, required: eligibleScreeningCount ?? 0 } : undefined
        }
        isAiAutomation
      />
    )
  }

  if (automationType === RequestAutomationType.FOR_PROVIDERS_IN_CATEGORY) {
    return (
      <TriggerActionApplyToSummary
        triggerIcon={<FilterListOutlined />}
        triggerPrimaryId="form.createAutomation.summary.companyMatchesCriteria"
        triggerExtra={
          <Box display="flex" flexWrap="wrap" gap={1} pt={1} pl={7}>
            {categoryFilters?.map(filter =>
              filter.filters.map((f, index) => (
                <FilterDisplayChip
                  key={`${filter.name}-${index}`}
                  filterName={filter.name}
                  filterValue={f.value}
                  operator={f.operator}
                />
              )),
            )}
          </Box>
        }
        actionIcon={<SendOutlined />}
        actionPrimaryId="form.createAutomation.summary.sendQuestionnaires"
        actionExtra={
          <Stack spacing={1.5} pt={1} pl={7}>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {templateNames.map(template => (
                <StyledChip key={template} label={template} />
              ))}
            </Box>
            <Box display="flex" flexWrap="wrap" gap={1}>
              <StyledChip label={periodDisplayName} />
              {((data.deadlineOffsetType && data.deadlineOffsetType !== 'custom') || data.customDueDate) && (
                <StyledChip
                  label={
                    data.customDueDate
                      ? formatDate(getDateWithoutTimeFromString(data.customDueDate as string), {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })
                      : formatMessage(
                          { id: automationTranslationKeyMap[automationType] },
                          { days: data.deadlineOffsetType },
                        )
                  }
                />
              )}
            </Box>
            {data.customDueDate && (
              <Alert severity="info" icon={<InfoOutlined />}>
                {formatMessage({ id: 'form.createAutomation.deadlineOffset.customDateWarning' })}
              </Alert>
            )}
          </Stack>
        }
        bannerMessageId="form.createAutomation.applyTo.immediateCategoryBanner"
        affectedCount={affectedCount}
        isLoadingCount={isLoadingAffectedCount}
      />
    )
  }

  if (automationType === RequestAutomationType.FOR_PROVIDERS_WITH_NEW_ASSESSMENT_LEVEL) {
    const filteredFilters = filters.filter(filter => filter.name !== 'assessments.isScreened')
    const assessmentChipLabels = getAssessmentChipLabels(filteredFilters, formatMessage, assessmentTemplates)

    const sourceChips =
      sourceFilter.length > 0
        ? (sourceFilter[0]?.filters.map((f, index) => (
            <StyledChip
              key={`source-${index}`}
              label={`${formatMessage({ id: 'schemas.request.source' })}: ${(() => {
                const actualValue = Array.isArray(f.value) ? f.value[0] : f.value
                return formatMessage({
                  id:
                    actualValue === 'true'
                      ? 'general.aiScreening'
                      : actualValue === 'false'
                        ? 'general.selfReported'
                        : 'general.unknown',
                })
              })()}`}
            />
          )) ?? [])
        : []

    return (
      <TriggerActionApplyToSummary
        triggerIcon={<AssessmentOutlined />}
        triggerPrimaryId="form.createAutomation.summary.assessmentCriteriaMatches"
        triggerExtra={
          <Box display="flex" flexWrap="wrap" gap={1} pt={1} pl={7}>
            {sourceChips}
            {assessmentChipLabels.map(label => (
              <StyledChip key={label} label={label} />
            ))}
            {riskAndCategoryChips.map((chip, index) => (
              <React.Fragment key={index}>{chip}</React.Fragment>
            ))}
          </Box>
        }
        actionIcon={<SendOutlined />}
        actionPrimaryId="form.createAutomation.summary.sendQuestionnaires"
        actionExtra={
          <Stack spacing={1.5} pt={1} pl={7}>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {templateNames.map(template => (
                <StyledChip key={template} label={template} />
              ))}
            </Box>
            <Box display="flex" flexWrap="wrap" gap={1}>
              <StyledChip label={periodDisplayName} />
              {((data.deadlineOffsetType && data.deadlineOffsetType !== 'custom') || data.customDueDate) && (
                <StyledChip
                  label={
                    data.customDueDate
                      ? formatDate(getDateWithoutTimeFromString(data.customDueDate as string), {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })
                      : formatMessage(
                          { id: automationTranslationKeyMap[automationType] },
                          { days: data.deadlineOffsetType },
                        )
                  }
                />
              )}
            </Box>
            {data.customDueDate && (
              <Alert severity="info" icon={<InfoOutlined />}>
                {formatMessage({ id: 'form.createAutomation.deadlineOffset.customDateWarning' })}
              </Alert>
            )}
          </Stack>
        }
        bannerMessageId="form.createAutomation.applyTo.immediateCategoryBanner"
        affectedCount={affectedCount}
        isLoadingCount={isLoadingAffectedCount}
      />
    )
  }

  return <RequestAutomationSummary filters={filters} translationKey={automationTranslationKeyMap[automationType]} />
}

export default AutomationSummary
